import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Bodega = { id: number; nombre: string; tipo: string; activo: boolean };
type InvMP = { id: number; bodegaNombre: string; especieNombre: string; kgDisponible: number; actualizadoEn: string };
// codigoLote es null cuando la mercancía entró por una factura de compra en
// vez de un lote de producción (negocio que revende sin fabricar).
type InvPT = { id: number; bodegaNombre: string; codigoLote: string | null; producto: string; cantidad: number; fechaVencimiento: string | null };
type Kardex = { id: number; tipoItem: string; itemRefId: number; tipoMovimiento: string; cantidad: number; costoUnitario: number; saldo: number; fecha: string; docReferencia: string | null };

const TIPO_BODEGA_LABEL: Record<string, string> = { materia_prima: "Materia prima", producto_terminado: "Producto terminado", mixta: "Mixta" };
const MOV_BADGE: Record<string, string> = { entrada: "bg-accent-light text-accent-dark", salida: "bg-red-50 text-red-700", ajuste: "bg-amber-50 text-amber-700" };

export function Inventarios() {
  const { tienePermiso } = useAuth();
  const puedeAjustar = tienePermiso("inventarios:ajustar");

  const [tab, setTab] = useState("materia-prima");
  const [bodegas, setBodegas] = useState<Bodega[]>([]);
  const [mp, setMp] = useState<InvMP[]>([]);
  const [pt, setPt] = useState<InvPT[]>([]);
  const [kardex, setKardex] = useState<Kardex[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [b, m, p, k] = await Promise.all([
        api.get("/inventarios/bodegas"),
        api.get("/inventarios/materia-prima"),
        api.get("/inventarios/producto-terminado"),
        api.get("/inventarios/kardex?limit=50"),
      ]);
      setBodegas(b); setMp(m); setPt(p); setKardex(k);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar inventarios.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  return (
    <Shell titulo="Inventarios" breadcrumb="Inventarios">
      <p className="text-sm text-textmuted mb-4">
        Existencias alimentadas automáticamente desde Compras (entradas de materia prima) y Producción (salidas de materia
        prima, entradas de producto terminado) y Comercial (salidas por facturación). El kardex es el libro auditable de todo movimiento.
      </p>
      <Tabs
        tabs={[
          { key: "materia-prima", label: "Materia prima" },
          { key: "producto-terminado", label: "Producto terminado" },
          { key: "kardex", label: "Kardex" },
          { key: "bodegas", label: "Bodegas" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "materia-prima" && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc"><th className="table-th">Bodega</th><th className="table-th">Especie</th><th className="table-th">Kg disponible</th><th className="table-th">Actualizado</th></tr></thead>
                <tbody>
                  {mp.map((r) => (
                    <tr key={r.id} className="border-b border-borderc last:border-0">
                      <td className="table-td">{r.bodegaNombre}</td>
                      <td className="table-td">{r.especieNombre}</td>
                      <td className="table-td font-semibold">{r.kgDisponible.toLocaleString()} kg</td>
                      <td className="table-td text-xs text-textmuted">{r.actualizadoEn}</td>
                    </tr>
                  ))}
                  {mp.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-8">Sin existencias de materia prima.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {tab === "producto-terminado" && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc"><th className="table-th">Producto</th><th className="table-th">Lote origen</th><th className="table-th">Bodega</th><th className="table-th">Cantidad</th><th className="table-th">Vencimiento</th></tr></thead>
                <tbody>
                  {pt.map((r) => (
                    <tr key={r.id} className="border-b border-borderc last:border-0">
                      <td className="table-td font-medium">{r.producto}</td>
                      <td className="table-td font-mono text-xs">{r.codigoLote || <span className="font-sans text-textmuted">Compra directa</span>}</td>
                      <td className="table-td">{r.bodegaNombre}</td>
                      <td className="table-td font-semibold">{r.cantidad.toLocaleString()}</td>
                      <td className="table-td text-xs text-textmuted">{r.fechaVencimiento || "—"}</td>
                    </tr>
                  ))}
                  {pt.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin existencias de producto terminado.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {tab === "kardex" && (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">Tipo</th><th className="table-th">Movimiento</th><th className="table-th">Cantidad</th><th className="table-th">Saldo</th><th className="table-th">Referencia</th></tr></thead>
                <tbody>
                  {kardex.map((k) => (
                    <tr key={k.id} className="border-b border-borderc last:border-0">
                      <td className="table-td text-xs text-textmuted whitespace-nowrap">{k.fecha}</td>
                      <td className="table-td">{k.tipoItem === "materia_prima" ? "Materia prima" : "Producto terminado"}</td>
                      <td className="table-td"><span className={`badge ${MOV_BADGE[k.tipoMovimiento]}`}>{k.tipoMovimiento}</span></td>
                      <td className="table-td">{k.cantidad.toLocaleString()}</td>
                      <td className="table-td font-semibold">{k.saldo.toLocaleString()}</td>
                      <td className="table-td font-mono text-xs text-textmuted">{k.docReferencia}</td>
                    </tr>
                  ))}
                  {kardex.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin movimientos registrados.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {tab === "bodegas" && <TabBodegas bodegas={bodegas} puedeAjustar={puedeAjustar} recargar={cargar} />}
        </>
      )}
    </Shell>
  );
}

function TabBodegas({ bodegas, puedeAjustar, recargar }: { bodegas: Bodega[]; puedeAjustar: boolean; recargar: () => void }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("mixta");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/inventarios/bodegas", { nombre, tipo });
      setModalAbierto(false);
      setNombre(""); setTipo("mixta");
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la bodega.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeAjustar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva bodega</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Nombre</th><th className="table-th">Tipo</th><th className="table-th">Estado</th></tr></thead>
          <tbody>
            {bodegas.map((b) => (
              <tr key={b.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{b.nombre}</td>
                <td className="table-td"><span className="badge bg-navy/10 text-navy">{TIPO_BODEGA_LABEL[b.tipo]}</span></td>
                <td className="table-td"><span className={`badge ${b.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{b.activo ? "activa" : "inactiva"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nueva bodega</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
              <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="materia_prima">Materia prima</option>
                <option value="producto_terminado">Producto terminado</option>
                <option value="mixta">Mixta</option>
              </select></div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
